import type { PresetDocument } from "@lpa/preset-model";
import type { ReferenceImage } from "./referenceImages";

export const MIN_COMPARISON_PRESETS = 2;
export const MAX_COMPARISON_PRESETS = 4;

export interface LocalPresetPackage {
  id: string;
  name: string;
  presetIds: string[];
  importedAt: string;
}

export interface ReferenceTestSet {
  id: "balanced" | "people" | "stress";
  name: string;
  description: string;
  referenceIds: string[];
}

export const referenceTestSets: ReferenceTestSet[] = [
  { id: "balanced", name: "Gebalanceerd", description: "Alle tien referenties", referenceIds: [] },
  { id: "people", name: "Huidtinten", description: "Drie verschillende portretten", referenceIds: ["portrait-daylight", "portrait-dark-skin", "backlight"] },
  { id: "stress", name: "Dynamisch bereik", description: "Schaduwen, highlights en kleur", referenceIds: ["shadows", "highlights", "calibration"] }
];

export function resolveTestSet(testSetId: ReferenceTestSet["id"], references: ReferenceImage[]): ReferenceImage[] {
  const testSet = referenceTestSets.find((item) => item.id === testSetId) ?? referenceTestSets[0]!;
  return testSet.referenceIds.length ? testSet.referenceIds.map((id) => references.find((item) => item.id === id)).filter((item): item is ReferenceImage => Boolean(item)) : references;
}

export function toggleComparisonPreset(selectedIds: string[], presetId: string): string[] {
  if (selectedIds.includes(presetId)) return selectedIds.length <= MIN_COMPARISON_PRESETS ? selectedIds : selectedIds.filter((id) => id !== presetId);
  if (selectedIds.length >= MAX_COMPARISON_PRESETS) return selectedIds;
  return [...selectedIds, presetId];
}

export function presetsByPackage(presets: PresetDocument[], packages: LocalPresetPackage[]): Array<{ package: LocalPresetPackage; presets: PresetDocument[] }> {
  const byId = new Map(presets.map((preset) => [preset.id, preset]));
  return packages.map((entry) => ({ package: entry, presets: entry.presetIds.map((id) => byId.get(id)).filter((preset): preset is PresetDocument => Boolean(preset)) })).filter((entry) => entry.presets.length > 0);
}

export function crossPackagePairs(packages: LocalPresetPackage[], similarities: Array<{ a: string; b: string; score: number }>): Array<{ a: string; b: string; score: number; packageA: string; packageB: string }> {
  const packageByPreset = new Map(packages.flatMap((entry) => entry.presetIds.map((id) => [id, entry] as const)));
  return similarities.flatMap((pair) => { const left = packageByPreset.get(pair.a); const right = packageByPreset.get(pair.b); return left && right && left.id !== right.id ? [{ ...pair, packageA: left.name, packageB: right.name }] : []; }).sort((a, b) => b.score - a.score || a.a.localeCompare(b.a) || a.b.localeCompare(b.b));
}
